<template>
  <tr
    :key="contactList.id"
    data-testid="outreach-contact-list-row"
  >
    <td>
      <div class="no-wrap q-pl-xs flex items-center">
        <BaseCheckbox
          v-if="!compact"
          :aria-label="contactList.name"
          data-testid="select-list-checkbox"
          :model-value="isSelected"
          new-design
          @update:model-value="emit('update:is-selected', contactList.id)"
        />
        <RouterLink
          class="c-table__title name-container full-width q-pl-sm tw:text-inherit tw:hover:underline tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary"
          data-testid="contact-list-link"
          :to="contactListRoute"
        >
          <BaseIcon
            v-if="contactList.private"
            icon="lock"
          />
          <BaseEllipseContent>
            {{ contactList.name }}
          </BaseEllipseContent>
        </RouterLink>
      </div>
    </td>
    <td>
      <div class="row justify-center">
        {{ contactList.contactListsContactsCount }}
      </div>
    </td>
    <td>
      <div class="row justify-center">
        {{ contactList.mediaReleasesContactListsCount }}
      </div>
    </td>
    <td>
      <div class="row justify-center">
        {{ contactList.pitchesContactListsCount }}
      </div>
    </td>
    <td>
      <div class="row justify-center">
        <InitialBubbles
          :names="[contactListCreatedBy]"
          use-fallback-color
        />
      </div>
    </td>
    <td>
      <BaseTags v-model="contactList.tags" />
    </td>
    <template v-if="!compact">
      <td>
        <div class="row justify-center">
          <InitialBubbles
            :names="[contactListUpdatedBy]"
            use-fallback-color
          />
        </div>
      </td>
      <td>
        <div class="row justify-center">
          <TimeAgo :date="contactList.updatedAt" />
        </div>
      </td>
      <td class="c-table__fit-content">
        <OutreachContactListActions
          class="no-wrap flex items-center justify-center"
          :contact-list="contactList"
          @action="emit('action', $event)"
        />
      </td>
    </template>
  </tr>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { type RouteLocationRaw } from "vue-router";

import { getLocaleText } from "shared/boot/i18n";
import { BaseEllipseContent, BaseIcon, BaseTags } from "shared/components/base";
import BaseCheckbox from "shared/components/core/forms/BaseCheckbox.vue";
import InitialBubbles from "shared/components/core/various/InitialBubbles.vue";
import TimeAgo from "shared/components/core/various/TimeAgo.vue";
import type ContactList from "shared/resources/ContactList";
import type { Maybe } from "shared/types";

import OutreachContactListActions from "./OutreachContactListActions.vue";

interface OutreachContactListRowProps {
  compact?: boolean;
  contactList: ContactList;
  isSelected?: boolean;
}

const props = defineProps<OutreachContactListRowProps>();

const emit = defineEmits<{
  action: [string];
  "update:is-selected": [Maybe<string>];
}>();

const contactListCreatedBy = computed<string>(
  () =>
    props.contactList.user?.name ||
    getLocaleText("outreach_contact_list_row.unknown")
);

const contactListUpdatedBy = computed<string>(
  () =>
    props.contactList.updatedByUser?.name ||
    getLocaleText("outreach_contact_list_row.unknown")
);

const contactListRoute = computed<RouteLocationRaw>(() => ({
  name: "outreach-lists-contacts",
  params: {
    listId: props.contactList.id,
  },
  query: {
    listLabel: props.contactList.name,
  },
}));
</script>

<style lang="scss" scoped>
.name-container {
  min-width: 0;
  display: flex;
  gap: var(--s-spacing-sm);
}

:deep(label.checkbox) {
  margin: 0;
}
</style>
